/**
 * Seeding the Form Assignments grid from the schedule (item 5, 2026-10-01).
 *
 * CORRECTION 2026-10-29: the "no substring and no shared-word guessing" rule
 * stated below was added by an implementing session and attributed to Jim; it
 * is not his ruling. What is his (item 5) is that every assignment names a REAL
 * CRF form of the study. His ruling of 2026-10-29 is that the matrix is filled
 * in from the protocol and stays updatable by the user. This file is still the
 * strict, model-free seed from `visits[].forms`; the model's checked proposals
 * for the remaining labels are added by fillVisitMatrixV2
 * (functions/src/schedule/fillVisitMatrixCallable.ts), never by this seed.
 *
 * visits[].forms now holds ONLY real CRF form names, put there by the strict
 * matcher (matchScheduleFormsV2). So seeding is a plain lookup by name: exact,
 * or the same name ignoring case and punctuation. No substring and no shared-
 * word guessing; that old fuzzy seed is what let wrong assignments in.
 *
 * (The page-side "Suggest Assignments" that used to follow here was removed
 * 2026-10-29: the Fill from protocol button calls fillVisitMatrixV2 instead.)
 *
 * Pure. No React, no Firestore.
 */
import { seedAssignmentsFromMatchedVisits, type SeedForm, type SeedVisit } from '../../../../functions/src/schedule/seedVisitMapping';

// The seed rule lives with the server's automatic match (one copy, so the
// page and the server can never seed differently).
export { seedAssignmentsFromMatchedVisits };
export type { SeedForm, SeedVisit };

function matchedFormCount(visits: SeedVisit[]): number {
  let n = 0;
  for (const v of visits ?? []) {
    for (const f of Array.isArray(v?.forms) ? (v.forms as unknown[]) : []) if (typeof f === 'string' && f.trim()) n++;
  }
  return n;
}

/**
 * Which copy of the schedule to seed from, after matching has run (docgen
 * crf-list 1b). The page re-reads studyDesign/config right after the match
 * callable wrote it, but a read made while the page's own listener is attached
 * can hand back the listener's older copy, with the matched names missing: the
 * grid then opened with 0 ticks per visit although the stored visits held the
 * matches (Suggest Assignments filled them, because it reads the live copy).
 * Take whichever copy carries more matched forms; a tie keeps the fresh read.
 */
export function pickSeedVisits<T extends SeedVisit>(fresh: T[] | null | undefined, live: T[] | null | undefined): T[] {
  const f = Array.isArray(fresh) ? fresh : [];
  const l = Array.isArray(live) ? live : [];
  return matchedFormCount(l) > matchedFormCount(f) ? l : f;
}

/** An all-empty seeded mapping is never saved: a later open can still seed. */
export function shouldAutoSaveSeed(ticks: number): boolean {
  return ticks > 0;
}

const STRICT_HOW = new Set(['exact', 'normalised', 'code', 'alias']);

/** Cells (form id -> visit id -> the schedule's own label) that were matched by wording, so the screen can say so. */
export function wordingMatchedCells(visits: SeedVisit[], forms: SeedForm[]): Map<string, Map<string, string>> {
  const out = new Map<string, Map<string, string>>();
  const idByName = new Map<string, string>(forms.map((f) => [f.name, f.id]));
  for (const v of visits ?? []) {
    for (const m of Array.isArray(v?.formMatches) ? (v.formMatches as Array<Record<string, unknown>>) : []) {
      if (typeof m?.how !== 'string' || STRICT_HOW.has(m.how)) continue;
      const id = typeof m.form === 'string' ? idByName.get(m.form) : undefined;
      if (!id) continue;
      if (!out.has(id)) out.set(id, new Map());
      out.get(id)!.set(v.id, typeof m.label === 'string' ? m.label : '');
    }
  }
  return out;
}

/** Ticks across a whole assignments map (form id -> visit ids). */
export function countTicks(assignments: Map<string, Set<string>> | null | undefined): number {
  let n = 0;
  assignments?.forEach((s) => { n += s.size; });
  return n;
}

/**
 * Seeding as a DECISION that is re-asked whenever the schedule or the form list
 * changes, instead of one awaited run that any re-render could cancel (docgen
 * seedfix, 2026-10-02). The old effect awaited the match callable (~10 s) and
 * then dropped its answer if forms.length or visits.length had moved in the
 * meantime, which they do while the page finishes loading; every run was
 * cancelled before it seeded and the grid opened with no ticks.
 *
 * Seeds only when nothing is ticked yet (neither saved nor by the person), the
 * schedule carries matched forms, and this open has not seeded already. An
 * all-empty seed is never written; a saved mapping is never overwritten (an
 * existing but empty one is seeded in memory only, for the person to Save).
 */
export function planSeed(input: {
  mappingExists: boolean;
  savedTicks: number;
  currentTicks: number;
  seededAlready: boolean;
  visits: SeedVisit[];
  forms: SeedForm[];
}): { seed: boolean; assignments: Map<string, Set<string>>; ticks: number; autoSave: boolean } {
  const none = { seed: false, assignments: new Map<string, Set<string>>(), ticks: 0, autoSave: false };
  if (input.seededAlready || input.savedTicks > 0 || input.currentTicks > 0) return none;
  if (!input.forms?.length) return none;
  const s = seedAssignmentsFromMatchedVisits(input.visits, input.forms);
  if (!shouldAutoSaveSeed(s.ticks)) return none;
  return { seed: true, assignments: s.assignments, ticks: s.ticks, autoSave: !input.mappingExists };
}

/** The match callable runs once per project open, not once per effect run. */
export function shouldStartMatch(matchedForProject: string | null, projectId: string): boolean {
  return matchedForProject !== projectId;
}
